import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { cloneDeep, filter, sortBy, uniqueId } from 'lodash'
import React, { FC, useCallback, useEffect, useState } from 'react'
import { useAlert } from 'react-alert'
import { Helmet } from 'react-helmet'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { RootState } from '../../../../states/containers/store'
import { createTaskCostSetting, fetchTaskCostSetting, updateTaskCostSetting } from '../../../../states/containers/task/taskSlice'
import LoadingSquare from '../../../components/common/LoadingSquare'
import Tips from '../../../components/common/Tips'
import CostModal from '../../../components/task/CostModal'
import ImportMemberCostModal from '../../../components/task/ImportMemberCostModal'
import { compile } from '../../../../utils/search'

interface UpdateCost {
  member_id: number
  hourly_pay: number | null
}

interface ModalState {
  id: number
  name: string
  cost: number | null
}

interface List {
  id: number
  name: string
  kana: string | null
  profile_image: string | null
  hourly_pay: number | null
}

const DISPLAY_BLOCK_NUMBER = 10

const Index: FC = () => {
  const {
    login,
    task,
  } = useSelector((state: RootState) => state)

  const location = useLocation()
  const dispatch = useDispatch()
  const alert = useAlert()

  const [searchWord, setSearchWord] = useState<string>("")
  const [data, setData] = useState<List[]>([])
  const [dataInit, setDataInit] = useState<List[]>([])

  const [isDisplayMember, setIsDisplayMember] = useState<boolean>(false)

  const initModalState = {
    id: 0,
    name: "",
    cost: null,
  }

  const [modalState, setModalState] = useState<ModalState>(initModalState)

  const [sortLabel, setSortLabel] = useState<string>("")

  const [isOpenImportModal, setIsOpenImportModal] = useState<boolean>(false)

  const [displayBlockNum, setDisplayBlockNum] = useState<number>(1)

  const currentScroll = () => Math.max(window.pageYOffset, document.documentElement.scrollTop, document.body.scrollTop)
  const bottomHeight = () => document.getElementsByClassName("main")[0].clientHeight
  const windowHeight = () => document.documentElement.clientHeight

  const onScroll = useCallback(() => {
    const adjustment = window.innerWidth >= 1280 ? 0.90 : 0.85
    if (
      (bottomHeight() * adjustment) < (currentScroll() + windowHeight())
      && data.length >= displayBlockNum * DISPLAY_BLOCK_NUMBER
    ) {
      setDisplayBlockNum((self) => self + 1)
    }
  }, [displayBlockNum, data])

  const sortDate = (dataList: List[]) => {
    const [key, order] = sortLabel.split("@")
    if (!key || !order) {
      setData(sortBy(dataList || [], 'id'))
      return
    }
    if (order === "asc") {
      setData(sortBy(dataList || [], key))
    } else if (order === "desc") {
      setData(sortBy(dataList || [], key).reverse())
    }
  }

  const search = (words: string) => {
    setSearchWord(words)
    if (!words) {
      setData(cloneDeep(dataInit))
      return
    }
    const word = words.toLowerCase().split(/\s+/)
    const list = filter(dataInit, (value) => {
      if (value === null) return false
      return (compile(word).test(value.name.toLowerCase() + value.kana))
    })
    sortDate(list)
  }

  const reset = () => {
    setSearchWord("")
    sortDate(dataInit)
  }

  const modCost = (id: number, cost: number | null) => {
    const memberDetail = task.taskCostSetting?.members.find((v) => v.id === id)
    if (!memberDetail) return
    dispatch(
      updateTaskCostSetting(
        {
          id: id,
          hourly_pay: cost,
        },
        () => {
          alert.success(`${memberDetail.name}さんの1時間あたりのコストを変更しました。`)
          setData((cur) => cur.map((v) => (v.id === id ? ({ ...v, hourly_pay: cost }) : v)))
          setDataInit((cur) => cur.map((v) => (v.id === id ? ({ ...v, hourly_pay: cost }) : v)))
          setIsDisplayMember(false)
        },
      ),
    )
  }

  const modCosts = (costData: UpdateCost[]) => {
    dispatch(
      createTaskCostSetting(
        { cost_settings: costData },
        () => {
          alert.success(`メンバーコストを一括登録しました。`)
          setIsDisplayMember(false)
        },
      ),
    )
  }

  const openMemberModal = (id: number) => {
    const memberDetail = task.taskCostSetting?.members.find((v) => v.id === id)
    if (!memberDetail) return
    setModalState({
      id: memberDetail.id,
      name: memberDetail.name,
      cost: memberDetail.hourly_pay,
    })
    setIsDisplayMember(true)
  }

  const buildSortArea = (type: string) => {
    if (sortLabel === `${type}@asc`) {
      return (
        <>
          <FontAwesomeIcon
            icon={['fas', 'sort-alpha-down']}
            style={{ cursor: "pointer" }}
            onClick={() => setSortLabel(`${type}@desc`)}
          />
        </>
      )
    }
    if (sortLabel === `${type}@desc`) {
      return (
        <>
          <FontAwesomeIcon
            icon={['fas', 'sort-alpha-down-alt']}
            style={{ cursor: "pointer" }}
            onClick={() => setSortLabel("")}
          />
        </>
      )
    }
    return (
      <>
        <FontAwesomeIcon
          icon={['fas', 'sort']}
          style={{ cursor: "pointer" }}
          onClick={() => setSortLabel(`${type}@asc`)}
        />
      </>
    )
  }

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [location])

  useEffect(() => {
    if (!login.accessToken) return
    dispatch(fetchTaskCostSetting())
  }, [login.accessToken])

  useEffect(() => {
    const members = sortBy(task.taskCostSetting?.members.map((v) => ({ ...v, hourly_pay: v.hourly_pay || 0 })) || [], 'id')
    setDataInit(members)
    if (!searchWord) {
      setData(members)
    }
  }, [task])

  useEffect(() => {
    sortDate(data)
  }, [sortLabel])

  useEffect(() => {
    document.addEventListener("scroll", onScroll)
    return () => {
      document.removeEventListener("scroll", onScroll)
    }
  }, [onScroll])

  return (
    <section className="task_page">
      <Helmet>
        <title>Qasee - 工数管理 - 設定 - メンバーコスト設定</title>
      </Helmet>
      <section className="breadcrumb">
        工数管理
        &nbsp;&gt;&nbsp;
        設定
        &nbsp;&gt;&nbsp;
        メンバーコスト設定
        &nbsp;
      </section>
      <section className="page_header">
        <h1>
          メンバーコスト設定
        </h1>
        <div className="tools">
          <div className="search_area">
            <input
              type="text"
              placeholder="メンバーを検索"
              onChange={(event) => search(event.target.value)}
              value={searchWord}
            />
            {
              searchWord && (
                <button type="button" className="reset" onClick={reset}>
                  <FontAwesomeIcon icon={['far', 'times-circle']} />
                </button>
              )
            }
          </div>
          <div className="button_area">
            <button
              type="button"
              className="button"
              onClick={() => setIsOpenImportModal(true)}
            >
              <FontAwesomeIcon icon={['fas', 'plus']} />
              &nbsp;
              メンバーコストを一括登録する
            </button>
          </div>
        </div>
      </section>
      <section className="page_description">
        <p>
          各メンバーの1時間あたりのコストを登録・変更できます。
        </p>
        <p>
          設定するとメンバーの業務時間に応じて、各プロジェクトにかかったコストを自動計算します。
        </p>
        <p>
          ※この設定は任意項目です。
        </p>
      </section>
      <section className="page_contents content-area">
        <div className="content-block">
          <div className="block-detail">
            {
              task.isLoading ? (
                <LoadingSquare>
                  <>
                    メンバーコスト設定を読み込んでいます…
                  </>
                </LoadingSquare>
              ) : (
                <>
                  <div className="cost_table">
                    <div className="row_head">
                      <div>
                        編集
                      </div>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "center",
                          gap: "4px",
                        }}
                      >
                        メンバー
                        {buildSortArea("kana")}
                      </div>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "center",
                          gap: "4px",
                        }}
                      >
                        1時間あたりのコスト
                        {buildSortArea("hourly_pay")}
                      </div>
                    </div>
                    {
                      data.slice(0, displayBlockNum * DISPLAY_BLOCK_NUMBER).map((member) => (
                        <div
                          className="row"
                          key={`cost_row_${member.id}`}
                        >
                          <div>
                            <Tips
                              description="コストを変更する"
                              className=""
                              icon={false}
                            >
                              <>
                                <FontAwesomeIcon
                                  icon={['fas', 'tools']}
                                  style={{ cursor: "pointer" }}
                                  onClick={() => openMemberModal(member.id)}
                                />
                              </>
                            </Tips>
                          </div>
                          <div>
                            {
                              member.profile_image ? (
                                <img alt="profile" src={member.profile_image} className="profile_icon" />
                              ) : (
                                <img
                                  alt=""
                                  src={`/static/images/profile/${(Number(member.id) || 0) % 12}.jpeg`}
                                  className="profile_icon"
                                />
                              )
                            }
                            <ruby>
                              {member.name}
                              <rt>{member.kana}</rt>
                            </ruby>
                          </div>
                          <div>
                            {member.hourly_pay ? `¥${member.hourly_pay.toLocaleString()}` : "-"}
                          </div>
                        </div>
                      ))
                    }
                  </div>
                </>
              )
            }
          </div>
        </div>
      </section>
      <CostModal
        modalState={modalState}
        closeCallback={() => setIsDisplayMember(false)}
        modCallback={(id, cost) => modCost(id, cost)}
        isDisplay={isDisplayMember}
      />
      <ImportMemberCostModal
        isDisplay={isOpenImportModal}
        memberList={task.taskCostSetting?.members || []}
        closeCallback={() => setIsOpenImportModal(false)}
        changeCallback={(costData) => modCosts(costData)}
      />
    </section>
  )
}

export default Index
